// bookingUtils.ts
import axios from 'axios';
import { toDateOnlyString } from '../../Utilities/date';


export const checkAvailability = async (packageId: number, dateRange: { startDate: Date | null, endDate: Date | null }) => {
    if (dateRange.startDate && dateRange.endDate) {
        const now = new Date();
        if (dateRange.startDate < now || dateRange.endDate < now) {
            throw new Error('Please select future dates.');
        }

    const formattedStartDate = toDateOnlyString(dateRange.startDate);
    const formattedEndDate = toDateOnlyString(dateRange.endDate);

        console.log('Formatted Start Date:', formattedStartDate);
        console.log('Formatted End Date:', formattedEndDate);

        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/PackageBooking/available-pricing-models`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
                    'Content-Type': 'application/json'
                },
                params: {
                    packageId: packageId,
                    startDate: formattedStartDate,
                    endDate: formattedEndDate
                }
            });

            const payload: any = response.data;
            console.log('API Response shape:', Array.isArray(payload) ? 'array' : typeof payload);

            // Normalize to an array regardless of server envelope shape
            const items = Array.isArray(payload)
                ? payload
                : Array.isArray(payload?.availabilityData)
                    ? payload.availabilityData
                    : Array.isArray(payload?.data)
                        ? payload.data
                        : Array.isArray(payload?.availability)
                            ? payload.availability
                            : [];

            return items;
        } catch (error) {
            console.error('Error fetching availability:', error);
            throw error;
        }
    }
    return null;
};

export interface ContinuousAvailabilityWarning {
    chaletType: string;
    note: string;
}

/**
 * Calls the calendar-availability endpoint for the exact selected date range and returns a
 * plain-language warning for any chalet type where NO single chalet is free for the whole
 * continuous stay (availableChalets === 0), even though the day-by-day calendar may show some
 * chalets free on individual days within the range (turnover between different units).
 *
 * This exists because the manager calendar grid renders availability PER DAY, which can make a
 * fully-booked-for-the-whole-stay range *look* available at a glance. Surfacing this note up
 * front - before a manager tries to submit a booking that will then be rejected with no visible
 * context - is what prevents the "calendar says available but booking fails" confusion.
 */
export const getContinuousAvailabilityWarnings = async (
    apiUrl: string,
    startDate: Date | null,
    endDate: Date | null
): Promise<ContinuousAvailabilityWarning[]> => {
    if (!startDate || !endDate) return [];

    const formattedStartDate = toDateOnlyString(startDate);
    const formattedEndDate = toDateOnlyString(endDate);

    try {
        const response = await axios.get(`${apiUrl}/PackageBooking/calendar-availability`, {
            headers: {
                'Authorization': `******'accessToken')}`,
                'Content-Type': 'application/json'
            },
            params: { startDate: formattedStartDate, endDate: formattedEndDate }
        });

        const data = response.data as { availabilityByType?: Array<{ chaletType: string; availableChalets: number; availabilityNote?: string }> };
        if (!data?.availabilityByType) return [];

        return data.availabilityByType
            .filter((t) => t.availableChalets === 0 && !!t.availabilityNote)
            .map((t) => ({ chaletType: t.chaletType, note: t.availabilityNote! }));
    } catch (error) {
        console.error('Error fetching continuous availability warnings:', error);
        return [];
    }
};

export const calculateTotalCost = (packages: any[], dateRange: { startDate: Date | null, endDate: Date | null }) => {
    if (!dateRange.startDate || !dateRange.endDate) return 0;
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    const timeDiff = endDate.getTime() - startDate.getTime();
    const numberOfNights = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return packages.reduce((total, campsite) => total + campsite.pricePerNight * numberOfNights, 0);
};